import { useEffect, useState, type PointerEvent as ReactPointerEvent } from "react";
import { CAR_SCREEN_HEIGHT, CAR_SCREEN_WIDTH } from "./CarScreenViewport";
import { assetUrl } from "../../utils/assetUrl";
import "./NightCards.css";

const PARCEL_DETAIL_IMAGE_SRC = assetUrl("assets/figma/night-ai/parcel-detail-screenshot.webp");
const POPUP_WIDTH = 650;
const POPUP_HEIGHT = 1336;
const POPUP_MARGIN = 24;
const INITIAL_POSITION = {
  x: 1432,
  y: 132
};

interface DragState {
  pointerId: number;
  scale: number;
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
}

export function ParcelDetailPopup({
  onClose
}: {
  onClose: () => void;
}) {
  const [position, setPosition] = useState(INITIAL_POSITION);
  const [dragState, setDragState] = useState<DragState | null>(null);

  useEffect(() => {
    if (!dragState) {
      return undefined;
    }

    const currentDragState = dragState;

    function handlePointerMove(event: PointerEvent) {
      if (event.pointerId !== currentDragState.pointerId) {
        return;
      }

      const nextX = clamp(
        currentDragState.startX + (event.clientX - currentDragState.startClientX) / currentDragState.scale,
        POPUP_MARGIN,
        CAR_SCREEN_WIDTH - POPUP_WIDTH - POPUP_MARGIN
      );
      const nextY = clamp(
        currentDragState.startY + (event.clientY - currentDragState.startClientY) / currentDragState.scale,
        POPUP_MARGIN,
        CAR_SCREEN_HEIGHT - POPUP_HEIGHT - POPUP_MARGIN
      );

      setPosition({ x: nextX, y: nextY });
    }

    function handlePointerUp(event: PointerEvent) {
      if (event.pointerId === currentDragState.pointerId) {
        setDragState(null);
      }
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [dragState]);

  function handlePointerDown(event: ReactPointerEvent<HTMLElement>) {
    const screenCanvas = event.currentTarget.closest(".desktop-stage__screen-canvas");
    const screenRect = screenCanvas?.getBoundingClientRect();
    const scale = screenRect && screenRect.width > 0 ? screenRect.width / CAR_SCREEN_WIDTH : 1;

    event.preventDefault();
    setDragState({
      pointerId: event.pointerId,
      scale,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: position.x,
      startY: position.y
    });
  }

  return (
    <section
      className={[
        "parcel-detail-popup",
        dragState ? "parcel-detail-popup--dragging" : ""
      ].filter(Boolean).join(" ")}
      aria-label="快递详情页"
      onPointerDown={handlePointerDown}
      style={{
        left: position.x,
        top: position.y
      }}
    >
      <img className="parcel-detail-popup__image" src={PARCEL_DETAIL_IMAGE_SRC} alt="我的快递详情页" draggable={false} />
      <button
        type="button"
        className="parcel-detail-popup__close"
        aria-label="关闭快递详情页"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={onClose}
      />
    </section>
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
